/**
 * IntelligenceDashboardCards.js
 *
 * Compact entry cards for the management / warehouse dashboard: "Stock risks"
 * (stockout-risk count + the two most urgent items; opens Stock Intelligence)
 * and "Margin watch" (products below target margin + the weakest one; opens Cost
 * Intelligence, management only). Each card is gated by its own access key and
 * the store's AI kill switch; a card whose endpoint fails simply stays hidden.
 *
 * @module components/management/IntelligenceDashboardCards
 */
import React, {useCallback, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {Card} from './ManagementUI';
import Icon from './SafeIcon';
import {getManagementScope} from '../../helpers/managementApi';
import {dashboardCardKey, readDashboardCard, writeDashboardCard} from '../../helpers/dashboardCardCache';
import {getMargins, getStockForecast} from '../../helpers/intelligenceApi';
import {formatQty, pickRiskItems, pickWorstMargin} from '../../helpers/intelligence';
import {formatKpiValue} from '../../helpers/businessAi';
import {useManagementAccess} from '../../helpers/managementAccess';
import {useManagementTheme} from '../../helpers/managementTheme';
import useAiAssistantGate from '../../screens/assistant/components/useAiAssistantGate';

/**
 * @returns {?JSX.Element} the cards, or `null` when nothing is accessible / loaded.
 */
export default function IntelligenceDashboardCards() {
    const {t} = useTranslation();
    const navigation = useNavigation();
    const m = useManagementTheme();
    const {canAccess} = useManagementAccess();
    const aiEnabled = useAiAssistantGate();
    const stockAllowed = aiEnabled && canAccess('stockIntelligence').allowed;
    // Margins are management-level figures: the reporting key (not the warehouse one) decides.
    const marginAllowed = aiEnabled && canAccess('costIntelligence').allowed && canAccess('businessDigest').allowed;

    const [stock, setStock] = useState(null);
    const [margin, setMargin] = useState(null);

    useFocusEffect(useCallback(() => {
        if (!stockAllowed && !marginAllowed) return undefined;
        let active = true;

        (async () => {
            const scope = await getManagementScope();
            // Fresh payload from a previous focus/mount (2 min TTL): skip the two catalog-wide forecasts.
            const cacheKey = dashboardCardKey('intel', scope, stockAllowed, marginAllowed);
            const cached = readDashboardCard(cacheKey);

            if (cached) {
                if (!active) return;
                setStock(cached.stock);
                setMargin(cached.margin);

                return;
            }

            // Independent, non-throwing calls ({ok}); Promise.all is Hermes-safe.
            const [stockRes, marginRes] = await Promise.all([
                stockAllowed ? getStockForecast({horizonDays: 7, limit: 50}, scope) : null,
                marginAllowed ? getMargins({days: 30, sort: 'marginPct', limit: 50}, scope) : null,
            ]);

            if (!active) return;
            const nextStock = stockRes?.ok && stockRes.data && Number.isFinite(Number(stockRes.data.summary?.stockoutRiskCount)) ? stockRes.data : null;
            const nextMargin = marginRes?.ok && marginRes.data && Number.isFinite(Number(marginRes.data.summary?.atRiskCount)) ? marginRes.data : null;

            setStock(nextStock);
            setMargin(nextMargin);
            if (nextStock || nextMargin) {
                writeDashboardCard(cacheKey, {stock: nextStock, margin: nextMargin});
            }
        })();

        return () => { active = false; };
    }, [stockAllowed, marginAllowed]));

    const showStock = stockAllowed && !!stock;
    const showMargin = marginAllowed && !!margin;

    if (!showStock && !showMargin) return null;

    const riskCount = showStock ? Number(stock.summary.stockoutRiskCount) : 0;
    const riskItems = showStock ? pickRiskItems(stock.items, 2) : [];
    const atRisk = showMargin ? Number(margin.summary.atRiskCount) : 0;
    const worst = showMargin ? pickWorstMargin(margin.items) : null;

    return (
        <>
            {showStock && (
                <TouchableOpacity activeOpacity={0.85} onPress={() => navigation.navigate('StockIntelligenceScreen')}>
                    <Card style={styles.card}>
                        <View style={styles.row}>
                            <Icon name={riskCount > 0 ? 'alert-octagon' : 'check-circle-outline'} size={22} color={riskCount > 0 ? '#F97316' : m.toneColor('success')} />
                            <View style={styles.flex}>
                                <Text style={[styles.headline, {color: m.colors.heading}]}>{t('intel.dash_stock_title')}</Text>
                                <Text style={[styles.label, {color: m.colors.subText}]}>
                                    {riskCount > 0 ? t('intel.dash_stock_risk', {count: riskCount}) : t('intel.dash_stock_none')}
                                </Text>
                            </View>
                            <Icon name="chevron-right" size={20} color={m.colors.subText} />
                        </View>
                        {riskItems.map((item) => (
                            <View key={String(item.stockId)} style={styles.itemRow}>
                                <Text style={[styles.label, styles.flex, {color: m.colors.heading}]} numberOfLines={1}>{item.name}</Text>
                                <Text style={[styles.value, {color: m.colors.subText}]} numberOfLines={1}>{formatQty(item.stockQty, item.unit)}</Text>
                            </View>
                        ))}
                    </Card>
                </TouchableOpacity>
            )}
            {showMargin && (
                <TouchableOpacity activeOpacity={0.85} onPress={() => navigation.navigate('CostIntelligenceScreen')}>
                    <Card style={[styles.card, styles.row]}>
                        <Icon name="chart-line" size={22} color={atRisk > 0 ? '#F97316' : m.toneColor('success')} />
                        <View style={styles.flex}>
                            <Text style={[styles.headline, {color: m.colors.heading}]}>{t('intel.dash_margin_title')}</Text>
                            <Text style={[styles.label, {color: m.colors.subText}]}>
                                {atRisk > 0 ? t('intel.dash_margin_risk', {count: atRisk}) : t('intel.dash_margin_none')}
                            </Text>
                            {!!worst && atRisk > 0 && (
                                <Text style={[styles.label, {color: m.colors.subText}]} numberOfLines={1}>
                                    {t('intel.dash_margin_worst', {name: worst.name, pct: formatKpiValue(worst.marginPct)})}
                                </Text>
                            )}
                        </View>
                        <Icon name="chevron-right" size={20} color={m.colors.subText} />
                    </Card>
                </TouchableOpacity>
            )}
        </>
    );
}

const styles = StyleSheet.create({
    card: {marginBottom: 12},
    row: {flexDirection: 'row', alignItems: 'center', gap: 12},
    flex: {flex: 1},
    headline: {fontSize: 15, fontWeight: '700', lineHeight: 21},
    label: {fontSize: 12.5, fontWeight: '600'},
    itemRow: {flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8, paddingLeft: 34},
    value: {fontSize: 13, fontWeight: '800'},
});
